/*
 * Copyright 2026 North Carolina State University
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { setupSessionTimeout, setupActivityListeners, clearSessionTimeout } from '../utils/sessionManager';

const AuthContext = createContext(null);

axios.defaults.withCredentials = true;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const login = async (credentials) => {
    try {
      const response = await axios.post('/api/auth/signin', credentials);
      const userData = response.data.user;
      setUser(userData);
      setupSessionTimeout(() => logout());
      setupActivityListeners(() => logout());
      return { success: true, mustChangePassword: !!response.data.mustChangePassword };
    } catch (error) {
      console.error('Login error:', error);
      const data = error.response?.data;
      setError(data?.message || 'An error occurred during login');
      return {
        success: false,
        error: data?.message || 'Login failed',
        requiresVerification: !!data?.requiresVerification,
        codeSendFailed: !!data?.codeSendFailed,
        email: data?.email
      };
    }
  };

  // Completes signup/sign-in after the user enters the code emailed to them.
  const verifyOtp = async (email, otp) => {
    try {
      const response = await axios.post('/api/auth/verify-otp', { email, otp });
      const userData = response.data.user;
      setUser(userData);
      setupSessionTimeout(() => logout());
      setupActivityListeners(() => logout());
      return { success: true, user: userData };
    } catch (error) {
      const data = error.response?.data;
      return { success: false, error: data?.message || data?.error || 'Verification failed' };
    }
  };

  const resendOtp = async (email) => {
    try {
      const response = await axios.post('/api/auth/resend-otp', { email });
      return { success: true, message: response.data?.message };
    } catch (error) {
      const data = error.response?.data;
      return { success: false, error: data?.message || data?.error || 'Could not resend code' };
    }
  };

  const logout = async () => {
    try {
      await axios.post('/api/auth/signout');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
      clearSessionTimeout();
    }
  };

  useEffect(() => {
    const checkSession = async () => {
      try {
        const response = await axios.get('/api/auth/session');
        if (response.data.user) {
          setUser(response.data.user);
          setupSessionTimeout(() => logout());
          setupActivityListeners(() => logout());
        }
      } catch (error) {
        if (error.response?.status !== 401) {
          console.error('Session check error:', error);
        }
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    checkSession();

    return () => {
      clearSessionTimeout();
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, error, login, logout, verifyOtp, resendOtp }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
